import { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { ChevronRight, Calendar, User, ArrowRight } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useMeta } from "@/hooks/useMeta";
import { useSettings } from "@/hooks/useSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const settings = useSettings();

  useMeta(
    "Aktuelles | BRIMO Immobilien NRW",
    "Aktuelle Beiträge, Marktberichte und Expertenwissen rund um Immobilien im Kreis Mettmann."
  );

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/blog`).then((r) => setPosts(r.data)).finally(() => setLoading(false));
  }, []);

  if (settings.aktuelles_enabled === false) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[#1a1a1a]" data-testid="blog-page">
      <Navbar />

      <section className="pt-32 pb-8">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <nav className="flex items-center gap-2 text-xs text-gray-500 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Start</Link>
            <ChevronRight size={10} />
            <span className="text-white">Aktuelles</span>
          </nav>
          <div className="h-0.5 w-10 bg-[#E61D38] mb-4" />
          <h1
            className="text-4xl sm:text-5xl font-black text-white uppercase tracking-tight leading-tight mb-4"
            style={{ fontFamily: "Montserrat, sans-serif" }}
            data-testid="blog-h1"
          >
            Aktuelles & Neuigkeiten
          </h1>
          <p className="text-gray-400 text-base max-w-2xl">
            Marktberichte, Expertentipps und Neuigkeiten rund um Immobilien im Kreis Mettmann.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          {loading ? (
            <p className="text-gray-500 text-sm">Laden...</p>
          ) : posts.length === 0 ? (
            <div className="text-center py-20" data-testid="blog-empty">
              <p className="text-gray-500 text-sm">Noch keine Beiträge vorhanden.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post) => {
                const date = new Date(post.created_at).toLocaleDateString("de-DE", { day: "numeric", month: "long", year: "numeric" });
                return (
                  <Link
                    key={post.id}
                    to={`/aktuelles/${post.slug}`}
                    className="group bg-[#0d0d0d] border border-white/5 overflow-hidden hover:border-[#E61D38]/30 transition-colors"
                    data-testid={`blog-card-${post.slug}`}
                  >
                    {post.image_url && (
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={post.image_url}
                          alt={post.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] to-transparent opacity-60" />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-3 text-[10px] text-gray-500 uppercase tracking-wider mb-3">
                        {post.category && (
                          <span className="text-[#E61D38] font-bold">{post.category}</span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar size={9} /> {date}
                        </span>
                      </div>
                      <h2 className="text-white font-bold text-base mb-2 group-hover:text-[#E61D38] transition-colors leading-snug">
                        {post.title}
                      </h2>
                      <p className="text-gray-500 text-xs leading-relaxed line-clamp-3 mb-4">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5 text-gray-600 text-[10px]">
                          <User size={9} /> {post.author}
                        </span>
                        <span className="flex items-center gap-1 text-[#E61D38] text-[10px] font-bold uppercase tracking-wider">
                          Lesen <ArrowRight size={9} className="transition-transform group-hover:translate-x-1" />
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
